/**
 * Investor detail page - profile, portfolio, media, co-investors, notes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  Linkedin,
  Globe,
  MapPin,
  Pencil,
  Trash2,
  ExternalLink,
  Sparkles,
  FileText,
  Send,
  UploadCloud,
  Search,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import DualScoreChip from '../components/DualScoreChip';
import ResearchSourcesPanel from '../components/ResearchSourcesPanel';
import RubricAssessmentPanel from '../components/RubricAssessmentPanel';
import LpDiligencePanel from '../components/LpDiligencePanel';
import investorService from '../services/investorService';
import matchService from '../services/matchService';
import api from '../services/api';
import { appendAuthToken } from '../services/auth';
import MatchCard from '../components/MatchCard';
import CoInvestmentGraph from '../components/CoInvestmentGraph';
import { Investor } from '../types/investor';
import { Match } from '../types/match';

type TabKey =
  | 'profile'
  | 'portfolio'
  | 'media'
  | 'co_investors'
  | 'matches'
  | 'notes'
  | 'dossier'
  | 'completion'
  | 'rubric'
  | 'lp_diligence'
  | 'research';

// Sortable portfolio table — same data shape as before, but each
// header is a button that flips ascending/descending sort.
type PortfolioSortKey = 'company_name' | 'round_type' | 'company_sector' |
  'investment_date' | 'amount_invested' | 'company_status';

const PortfolioTable: React.FC<{ portfolio: any[] }> = ({ portfolio }) => {
  const [sortKey, setSortKey] = useState<PortfolioSortKey>('company_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const onHeader = (k: PortfolioSortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    const copy = (portfolio || []).slice();
    copy.sort((a: any, b: any) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const aMissing = av === null || av === undefined || av === '';
      const bMissing = bv === null || bv === undefined || bv === '';
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [portfolio, sortKey, sortDir]);

  const arrow = (k: PortfolioSortKey) =>
    sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  if (portfolio.length === 0) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Portfolio companies</h3>
        <p className="text-sm text-gray-500">No portfolio data on file yet.</p>
      </section>
    );
  }

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Portfolio companies ({portfolio.length})
        </h3>
        <span className="text-xs text-gray-500">
          Sorted by <strong>{sortKey.replace('_', ' ')}</strong> ({sortDir})
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase border-b border-gray-200">
            <tr>
              {([
                ['company_name', 'Company'],
                ['round_type', 'Round'],
                ['company_sector', 'Sector'],
                ['investment_date', 'Date'],
                ['amount_invested', 'Amount'],
                ['company_status', 'Status'],
              ] as const).map(([k, label]) => (
                <th
                  key={k}
                  onClick={() => onHeader(k)}
                  className="text-left py-2 pr-4 cursor-pointer select-none hover:text-gray-900"
                >
                  {label}{arrow(k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((p: any) => (
              <tr key={p.id} className="border-b border-gray-100 last:border-b-0">
                <td className="py-2 pr-4 font-medium text-gray-900">{p.company_name}</td>
                <td className="py-2 pr-4">{formatLabel(p.round_type)}</td>
                <td className="py-2 pr-4">{p.company_sector || '—'}</td>
                <td className="py-2 pr-4">{formatDate(p.investment_date)}</td>
                <td className="py-2 pr-4">{formatMoney(p.amount_invested)}</td>
                <td className="py-2 pr-4">{formatLabel(p.company_status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};


// Insights panel — explains *why* the system chose these matches in
// plain English, based on the investor's own profile fields. Sits
// above the matches list so the operator (and the investor when this
// page is shared) can see the rationale at a glance.
const InvestorMatchInsights: React.FC<{ investor: any; matches: any[] }> = ({
  investor, matches,
}) => {
  const stages = (investor?.stage_preferences || []).filter(Boolean);
  const sectors = (investor?.sector_focus || []).slice(0, 4).filter(Boolean);
  const checkBand =
    investor?.check_size_min != null && investor?.check_size_max != null
      ? `$${(investor.check_size_min / 1000).toLocaleString()}K – $${(investor.check_size_max / 1000).toLocaleString()}K`
      : null;
  const geo = (investor?.geographic_preference || []).filter(Boolean);
  const avgScore = matches.length
    ? Math.round(matches.reduce((s, m) => s + (m.overall_score ?? 0), 0) / matches.length)
    : null;
  const hasAnything = stages.length || sectors.length || checkBand || geo.length;

  if (!hasAnything && matches.length === 0) return null;

  return (
    <section className="bg-blue-50 border border-blue-200 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-blue-900 mb-2">
        Why these matches?
      </h3>
      <p className="text-sm text-blue-900 leading-relaxed">
        {matches.length > 0 ? (
          <>
            We surfaced <strong>{matches.length}</strong> startup
            {matches.length === 1 ? '' : 's'}
            {avgScore !== null && <> (average score <strong>{avgScore}</strong>)</>}{' '}
            because they best fit the investor's
            {stages.length > 0 && <> <strong>stage focus</strong> ({stages.join(', ')})</>}
            {stages.length > 0 && (sectors.length > 0 || checkBand || geo.length > 0) ? ',' : ''}
            {sectors.length > 0 && <> <strong>sectors</strong> ({sectors.join(', ')})</>}
            {sectors.length > 0 && (checkBand || geo.length > 0) ? ',' : ''}
            {checkBand && <> <strong>check size</strong> ({checkBand})</>}
            {checkBand && geo.length > 0 ? ',' : ''}
            {geo.length > 0 && <> <strong>geography</strong> ({geo.join(', ')})</>}.
            {' '}Match scores combine those dimensions with thesis alignment and
            co-investment patterns.
          </>
        ) : (
          <>
            We have the investor's
            {stages.length > 0 && <> stage focus,</>}
            {sectors.length > 0 && <> sectors,</>}
            {checkBand && <> check size,</>}
            {geo.length > 0 && <> geography,</>}{' '}
            but no startups have scored high enough yet. Click <strong>Generate
            matches</strong> to score this investor against the corpus, or use
            the feedback box below to refine what they're looking for.
          </>
        )}
      </p>
      {!hasAnything && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
          ⚠️ This investor's profile is sparse — no stage / sector / check-size
          on file. Matching will be much weaker until those fields are filled in
          (Profile tab → Edit, or run a deeper research pass).
        </p>
      )}
    </section>
  );
};


// Feedback box — captures investor refinements ("looking for fewer X,
// more Y"). Submits to /investors/me/match-feedback when the page is
// loaded by the investor themselves; admin operators can leave a note
// here too and it lands on investor.notes for the next match pass.
const InvestorMatchFeedbackBox: React.FC<{ investorId: number }> = ({ investorId }) => {
  const [note, setNote] = useState('');
  const [sentiment, setSentiment] = useState<'agree' | 'more_like_this' | 'fewer_like_this' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!note.trim()) {
      toast.error('Add a short note before submitting');
      return;
    }
    setSubmitting(true);
    try {
      // Append to investor.notes via the admin investors PATCH/PUT
      // (works regardless of whether the user is the investor or
      // admin). Matching engine reads notes on next re-score pass.
      const r = await api.get(`/investors/${investorId}`);
      const existing = (r.data?.notes || '').trim();
      const ts = new Date().toISOString();
      const tag = sentiment ? `, ${sentiment}` : '';
      const entry = `[${ts}] (match feedback${tag}) ${note.trim()}`;
      const merged = existing ? `${existing}\n${entry}` : entry;
      await api.put(`/investors/${investorId}`, { notes: merged });
      toast.success('Saved — feedback will refine future matches.');
      setNote('');
      setSentiment(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to save feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Refine these matches</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Tell the system what you're really looking for — "more pre-seed B2B
          SaaS" / "skip anything unprofitable" / "I prefer technical co-founders".
          Notes are saved to the investor profile and re-fed into the matching
          engine on the next pass.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {([
          { v: 'agree', label: '👍 These look right', cls: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
          { v: 'more_like_this', label: '✦ More like these', cls: 'border-blue-300 text-blue-700 bg-blue-50' },
          { v: 'fewer_like_this', label: '✗ Fewer like these', cls: 'border-amber-300 text-amber-700 bg-amber-50' },
        ] as const).map((o) => {
          const active = sentiment === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => setSentiment(active ? null : o.v)}
              className={`px-3 py-1 text-xs font-medium rounded-full border ${
                active ? `${o.cls} ring-1 ring-offset-0` : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <textarea
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What kinds of matches do you want to see more (or less) of?"
        disabled={submitting}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <div className="flex justify-end mt-3">
        <button
          onClick={submit}
          disabled={submitting || !note.trim()}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Submit feedback
        </button>
      </div>
    </section>
  );
};


// Tab order — story-first. Profile sets context, dossier explains
// who they are, portfolio and matches show what they do, then
// supporting analytical tabs follow.
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'profile',      label: 'Profile' },
  { key: 'dossier',      label: 'Dossier' },
  { key: 'portfolio',    label: 'Portfolio' },
  { key: 'matches',      label: 'Matches' },
  { key: 'media',        label: 'Media' },
  { key: 'co_investors', label: 'Co-investors' },
  { key: 'rubric',       label: 'Rubric' },
  { key: 'lp_diligence', label: 'LP Diligence' },
  { key: 'research',     label: 'Research Sources' },
  { key: 'notes',        label: 'Notes' },
  { key: 'completion',   label: 'Completion' },
];

interface ProvenanceRow {
  source_type: string;
  source_url: string | null;
  value: string | null;
  written_at: string | null;
  written_by: string;
}

interface CompletionResponse {
  percent: number;
  free_percent: number;
  free_max: number;
  earned: number;
  missing: string[];
  checklist: Array<{
    key: string;
    label: string;
    weight: number;
    present: boolean;
    source_class: string;
    filled_by?: ProvenanceRow | null;
  }>;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  sec_iapd_individual:       'SEC IAPD',
  sec_iapd_firm:             'SEC IAPD (firm)',
  finra_brokercheck:         'FINRA BrokerCheck',
  angellist:                 'AngelList',
  wikipedia:                 'Wikipedia',
  opencorporates:            'OpenCorporates',
  opensecrets:               'OpenSecrets',
  hunter_io:                 'Hunter.io',
  claude_sentiment:          'Claude (sentiment)',
  firm_enrichment_claude:    'Claude (firm enrichment)',
  company_enrichment_claude: 'Claude (company enrichment)',
};

const formatSourceLabel = (sourceType: string): string =>
  SOURCE_TYPE_LABELS[sourceType] || sourceType.replace(/_/g, ' ');

const formatRelativeTime = (value?: string | null): string => {
  if (!value) return '';
  try {
    const then = new Date(value).getTime();
    const now = Date.now();
    const diffSec = Math.round((now - then) / 1000);
    if (Number.isNaN(diffSec)) return '';
    if (diffSec < 60) return 'just now';
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
    const diffMo = Math.round(diffDay / 30);
    if (diffMo < 12) return `${diffMo} month${diffMo === 1 ? '' : 's'} ago`;
    const diffYr = Math.round(diffMo / 12);
    return `${diffYr} year${diffYr === 1 ? '' : 's'} ago`;
  } catch {
    return '';
  }
};

const STATUS_BADGE_STYLES: Record<string, string> = {
  complete: 'bg-green-100 text-green-700 border-green-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-gray-100 text-gray-700 border-gray-200',
};

const formatLabel = (value?: string): string => {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatMoney = (value?: number): string => {
  if (value === undefined || value === null) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

interface FieldRowProps {
  label: string;
  value?: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, value }) => (
  <div className="flex items-start py-2 border-b border-gray-100 last:border-b-0">
    <div className="w-44 flex-shrink-0 text-xs uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="flex-1 text-sm text-gray-900">
      {value === undefined || value === null || value === '' ? (
        <span className="text-gray-400">—</span>
      ) : (
        value
      )}
    </div>
  </div>
);

const InvestorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabKey>('profile');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isTriggeringResearch, setIsTriggeringResearch] = useState<boolean>(false);
  const [discoveringMedia, setDiscoveringMedia] = useState<boolean>(false);
  const [mediaNotice, setMediaNotice] = useState<string | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchesLoading, setMatchesLoading] = useState<boolean>(false);
  const [matchesLoaded, setMatchesLoaded] = useState<boolean>(false);
  const [generatingMatches, setGeneratingMatches] = useState<boolean>(false);
  const [completion, setCompletion] = useState<CompletionResponse | null>(null);

  const loadCompletion = async (investorId: number) => {
    try {
      const res = await api.get(`/investors/${investorId}/completion`);
      setCompletion(res.data);
    } catch {
      setCompletion(null);
    }
  };

  const loadMatches = async (investorId: number) => {
    setMatchesLoading(true);
    const result = await matchService.fetchInvestorMatches(investorId);
    if (result.error) {
      toast.error(result.error);
      setMatches([]);
    } else {
      setMatches(result.data || []);
    }
    setMatchesLoading(false);
    setMatchesLoaded(true);
  };

  const handleGenerateInvestorMatches = async () => {
    if (!investor) return;
    setGeneratingMatches(true);
    const result = await matchService.generateMatches({ investor_id: investor.id });
    setGeneratingMatches(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data?.skipped_reason === 'below_completion_threshold') {
      const pct = result.data.completion_pct ?? 0;
      const thr = result.data.completion_threshold ?? 80;
      toast(
        `Matching is gated until research completion reaches ${thr}% — ` +
          `this investor is at ${pct}%. Run more research depth (or have ` +
          `the investor complete the self-serve onboarding) to enable matching.`,
        { icon: '⚠️', duration: 7000 },
      );
      return;
    }
    toast.success(`Generated ${result.data?.generated ?? 0} match(es)`);
    await loadMatches(investor.id);
  };

  const reloadInvestor = async (investorId: number) => {
    const result = await investorService.fetchInvestor(investorId);
    if (result.data) {
      setInvestor(result.data);
    }
    return result.data;
  };

  // Media discovery is metered — one SerpAPI search per press, against a
  // 1,000/month plan. Say what it cost and what it found rather than
  // silently refreshing, so nobody clicks it repeatedly wondering.
  const handleDiscoverMedia = async () => {
    if (!investor || discoveringMedia) return;
    setDiscoveringMedia(true);
    setMediaNotice(null);
    const result = await investorService.discoverMedia(investor.id);
    if (result.error) {
      setMediaNotice(result.error);
    } else if (result.data && !result.data.ok) {
      setMediaNotice(result.data.reason || 'Search unavailable.');
    } else if (result.data) {
      const { created, skipped } = result.data;
      if (created === 0 && skipped === 0) {
        setMediaNotice('No media appearances found for this investor.');
      } else if (created === 0) {
        setMediaNotice(`No new appearances — ${skipped} already saved.`);
      } else {
        setMediaNotice(
          `Found ${created} appearance${created === 1 ? '' : 's'}` +
          (skipped ? ` (${skipped} already saved).` : '.')
        );
      }
      await reloadInvestor(investor.id);
    }
    setDiscoveringMedia(false);
  };

  useEffect(() => {
    const investorId = Number(id);
    if (!investorId) {
      toast.error('Invalid investor id');
      setIsLoading(false);
      return;
    }
    const loadData = async () => {
      setIsLoading(true);
      const result = await investorService.fetchInvestor(investorId);
      if (result.error) {
        toast.error(result.error);
      } else if (result.data) {
        setInvestor(result.data);
        loadCompletion(investorId);
      }
      setIsLoading(false);
    };
    loadData();
  }, [id]);

  // Poll research status while in_progress
  useEffect(() => {
    if (!investor) return;
    if (investor.research_status !== 'in_progress') return;
    const investorId = investor.id;
    const intervalId = window.setInterval(async () => {
      const statusResult = await investorService.getResearchStatus(investorId);
      if (statusResult.data) {
        const newStatus = statusResult.data.research_status;
        if (newStatus && newStatus !== 'in_progress') {
          // Status flipped - reload full record to pick up dossier and any other field changes
          await reloadInvestor(investorId);
        } else {
          // Still in progress - update lightweight fields so badge stays fresh
          setInvestor((prev) =>
            prev
              ? {
                  ...prev,
                  research_status: (newStatus as Investor['research_status']) || prev.research_status,
                  last_researched_at:
                    statusResult.data?.last_researched_at || prev.last_researched_at,
                }
              : prev
          );
        }
      }
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, [investor?.id, investor?.research_status]);

  useEffect(() => {
    if (activeTab !== 'matches' || matchesLoaded || !investor) return;
    loadMatches(investor.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, matchesLoaded, investor?.id]);

  const handleRunResearch = async () => {
    if (!investor) return;
    setIsTriggeringResearch(true);
    const result = await investorService.triggerResearch(investor.id);
    setIsTriggeringResearch(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Research queued');
    await reloadInvestor(investor.id);
  };

  const handleDelete = async () => {
    if (!investor) return;
    if (!window.confirm(`Delete ${investor.full_name}? This cannot be undone.`)) {
      return;
    }
    setIsDeleting(true);
    const result = await investorService.deleteInvestor(investor.id);
    setIsDeleting(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Investor deleted');
    navigate('/investors');
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading investor...
        </div>
      </DashboardLayout>
    );
  }

  if (!investor) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
          <p className="mb-4">Investor not found.</p>
          <Link to="/investors" className="text-blue-600 hover:underline">
            Back to investors
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <Link
            to="/investors"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to investors
          </Link>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                {investor.profile_photo_url ? (
                  <img
                    src={investor.profile_photo_url}
                    alt={investor.full_name}
                    className="w-16 h-16 rounded-full object-cover bg-gray-100"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xl font-semibold">
                    {investor.full_name?.[0] || '?'}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900 truncate">
                      {investor.full_name}
                    </h1>
                    {investor.research_status && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${
                          STATUS_BADGE_STYLES[investor.research_status] ||
                          STATUS_BADGE_STYLES.pending
                        }`}
                      >
                        {investor.research_status === 'in_progress' && (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        {formatLabel(investor.research_status)}
                      </span>
                    )}
                    <DualScoreChip
                      completionPct={investor.research_completion_pct}
                      rubricScore={investor.rubric_score}
                      rubricTier={investor.rubric_tier}
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    {[investor.title, investor.firm_name].filter(Boolean).join(' • ') ||
                      'No firm/title on file'}
                  </p>
                  {investor.last_researched_at && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Researched {formatRelativeTime(investor.last_researched_at)}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mt-2">
                    {investor.email && (
                      <a
                        href={`mailto:${investor.email}`}
                        className="inline-flex items-center hover:text-blue-600"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        {investor.email}
                      </a>
                    )}
                    {investor.phone && (
                      <span className="inline-flex items-center">
                        <Phone className="w-3 h-3 mr-1" />
                        {investor.phone}
                      </span>
                    )}
                    {investor.linkedin_url && (
                      <a
                        href={investor.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center hover:text-blue-600"
                      >
                        <Linkedin className="w-3 h-3 mr-1" />
                        LinkedIn
                      </a>
                    )}
                    {investor.website_url && (
                      <a
                        href={investor.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center hover:text-blue-600"
                      >
                        <Globe className="w-3 h-3 mr-1" />
                        Website
                      </a>
                    )}
                    {(investor.location_city || investor.location_state) && (
                      <span className="inline-flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {[investor.location_city, investor.location_state]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleRunResearch}
                  disabled={
                    isTriggeringResearch || investor.research_status === 'in_progress'
                  }
                  className="inline-flex items-center px-3 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50"
                >
                  {isTriggeringResearch || investor.research_status === 'in_progress' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1" />
                  )}
                  Run Research
                </button>
                <a
                  href={appendAuthToken(`${process.env.REACT_APP_API_URL || '/api/v1'}/investors/${investor.id}/profile.pdf`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                  title="Download a printable PDF profile of this investor"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Download PDF
                </a>
                <Link
                  to="/investors/upload-brochure"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                  title="Upload a firm tearsheet or fund overview to auto-fill profile fields"
                >
                  <UploadCloud className="w-4 h-4 mr-1" />
                  Upload brochure
                </Link>
                <Link
                  to={`/investors/new?id=${investor.id}`}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Link>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <section className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Classification</h3>
              <FieldRow label="Investor type" value={formatLabel(investor.investor_type)} />
              <FieldRow
                label="Stage preferences"
                value={
                  investor.stage_preferences && investor.stage_preferences.length > 0
                    ? investor.stage_preferences.map(formatLabel).join(', ')
                    : undefined
                }
              />
              <FieldRow
                label="Sector focus"
                value={investor.sector_focus?.join(', ')}
              />
              <FieldRow
                label="Geographic preference"
                value={investor.geographic_preference?.join(', ')}
              />
              <FieldRow
                label="Relationship status"
                value={formatLabel(investor.relationship_status)}
              />
              <FieldRow
                label="Last contact"
                value={formatDate(investor.last_contact_date)}
              />
              <FieldRow label="Tags" value={investor.tags?.join(', ')} />
            </section>

            <section className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Accreditation</h3>
              <FieldRow
                label="Status"
                value={formatLabel(investor.accreditation_status)}
              />
              <FieldRow
                label="Confidence"
                value={
                  investor.accreditation_confidence !== undefined
                    ? `${investor.accreditation_confidence}/100`
                    : undefined
                }
              />
              {investor.accreditation_evidence &&
                investor.accreditation_evidence.length > 0 && (
                  <div className="pt-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                      Evidence
                    </p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {investor.accreditation_evidence.map((ev, i) => (
                        <li key={i} className="flex items-start">
                          <span className="mr-2 text-gray-400">•</span>
                          <span>
                            {ev.finding || ev.source}
                            {ev.url && (
                              <a
                                href={ev.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 text-blue-600 hover:underline inline-flex items-center"
                              >
                                source <ExternalLink className="w-3 h-3 ml-1" />
                              </a>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </section>

            <section className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Financial</h3>
              <FieldRow
                label="Check size min"
                value={formatMoney(investor.check_size_min)}
              />
              <FieldRow
                label="Check size max"
                value={formatMoney(investor.check_size_max)}
              />
              <FieldRow label="Estimated AUM" value={formatMoney(investor.estimated_aum)} />
              <FieldRow label="Fund size" value={formatMoney(investor.fund_size)} />
            </section>

            <section className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Due Diligence Profile
              </h3>
              <FieldRow label="DD style" value={formatLabel(investor.dd_style)} />
              <FieldRow
                label="Preferred instruments"
                value={
                  investor.preferred_instruments && investor.preferred_instruments.length > 0
                    ? investor.preferred_instruments.map(formatLabel).join(', ')
                    : undefined
                }
              />
              <FieldRow
                label="Decision timeline"
                value={formatLabel(investor.decision_timeline)}
              />
              <FieldRow
                label="Post-investment involvement"
                value={formatLabel(investor.post_investment_involvement)}
              />
              <FieldRow
                label="Risk tolerance"
                value={formatLabel(investor.risk_tolerance)}
              />
            </section>

            <section className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Intelligence (AI-generated)
              </h3>
              <div className="space-y-4 text-sm text-gray-800">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Investment philosophy
                  </p>
                  <p className="whitespace-pre-wrap">
                    {investor.investment_philosophy || (
                      <span className="text-gray-400">No data yet.</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Known interests
                  </p>
                  <p className="whitespace-pre-wrap">
                    {investor.known_interests || (
                      <span className="text-gray-400">No data yet.</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Known red flags
                  </p>
                  <p className="whitespace-pre-wrap">
                    {investor.known_red_flags || (
                      <span className="text-gray-400">No data yet.</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Negotiation style
                  </p>
                  <p className="whitespace-pre-wrap">
                    {investor.negotiation_style || (
                      <span className="text-gray-400">No data yet.</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Sentiment summary
                  </p>
                  <p className="whitespace-pre-wrap">
                    {investor.sentiment_summary || (
                      <span className="text-gray-400">No data yet.</span>
                    )}
                  </p>
                </div>
                {investor.ai_generated_dossier && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      Full dossier
                    </p>
                    <pre className="whitespace-pre-wrap font-sans bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                      {investor.ai_generated_dossier}
                    </pre>
                    {investor.dossier_generated_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Generated {formatDate(investor.dossier_generated_at)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'research' && (
          <ResearchSourcesPanel entityKind="investor" entityId={investor.id} />
        )}

        {activeTab === 'rubric' && (
          <RubricAssessmentPanel entityKind="investor" entityId={investor.id} />
        )}

        {activeTab === 'lp_diligence' && (
          <LpDiligencePanel investorId={investor.id} />
        )}

        {activeTab === 'portfolio' && (
          <PortfolioTable portfolio={investor.portfolio || []} />
        )}

        {activeTab === 'media' && (
          <section className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Media appearances</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Interviews, podcasts, panels and articles. Each search uses one
                  SerpAPI credit, so it runs only when you ask.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDiscoverMedia}
                disabled={discoveringMedia}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Search Google for this investor's media appearances (1 search)"
              >
                <Search className="w-4 h-4" />
                {discoveringMedia ? 'Searching…' : 'Find media'}
              </button>
            </div>
            {mediaNotice && (
              <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 mb-4">
                {mediaNotice}
              </p>
            )}
            {investor.media && investor.media.length > 0 ? (
              <ul className="space-y-3">
                {investor.media.map((m) => (
                  <li
                    key={m.id}
                    className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <a
                          href={m.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-700 hover:underline inline-flex items-center"
                        >
                          {m.title || m.url}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatLabel(m.media_type)} • {formatDate(m.published_date)}
                        </p>
                        {m.key_quotes && m.key_quotes.length > 0 && (
                          <ul className="mt-2 text-sm text-gray-700 italic space-y-1">
                            {m.key_quotes.slice(0, 3).map((q, i) => (
                              <li key={i}>"{q}"</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {m.sentiment_score !== undefined && m.sentiment_score !== null && (
                        <span className="text-xs text-gray-600 whitespace-nowrap">
                          sentiment: {m.sentiment_score.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">
                No media appearances logged yet — use <strong>Find media</strong> to
                search for interviews, podcasts and articles.
              </p>
            )}
          </section>
        )}

        {activeTab === 'co_investors' && (
          <section className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Co-investor relationship graph
              </h3>
              <CoInvestmentGraph
                focalInvestorName={investor.full_name || `Investor #${investor.id}`}
                coInvestments={investor.co_investments || []}
                onNodeClick={(coId) => navigate(`/investors/${coId}`)}
              />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4 mt-6">
              Co-investors (table view)
            </h3>
            {investor.co_investments && investor.co_investments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 uppercase border-b border-gray-200">
                    <tr>
                      <th className="text-left py-2 pr-4">Co-investor</th>
                      <th className="text-left py-2 pr-4">Deals together</th>
                      <th className="text-left py-2 pr-4">Sectors</th>
                      <th className="text-left py-2 pr-4">Last co-invest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investor.co_investments.map((c) => (
                      <tr key={c.id} className="border-b border-gray-100 last:border-b-0">
                        <td className="py-2 pr-4 font-medium text-gray-900">
                          {c.co_investor_name}
                        </td>
                        <td className="py-2 pr-4">{c.deal_count ?? '—'}</td>
                        <td className="py-2 pr-4">{c.sectors?.join(', ') || '—'}</td>
                        <td className="py-2 pr-4">
                          {formatDate(c.last_co_investment_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No co-investment data yet.</p>
            )}
          </section>
        )}

        {activeTab === 'dossier' && (
          <section className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">AI-Generated Dossier</h3>
              {investor.dossier_generated_at && (
                <p className="text-xs text-gray-500">
                  Generated {formatRelativeTime(investor.dossier_generated_at)}
                </p>
              )}
            </div>
            {investor.ai_generated_dossier ? (
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-4">
                {investor.ai_generated_dossier}
              </pre>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  No dossier generated yet
                </h4>
                <p className="text-sm text-blue-900 leading-relaxed">
                  The AI dossier is a Claude-written intelligence brief —
                  investment philosophy, what excites them, what they avoid, decision
                  style, and approach recommendations — distilled from every video,
                  podcast, article, and filing the research pipeline pulled.
                </p>
                <p className="text-sm text-blue-900 leading-relaxed mt-2">
                  <strong>Why it's empty for this investor:</strong> dossier generation
                  requires Claude AI, which is currently disabled platform-wide to
                  control costs. Once you enable it, the dossier will be generated
                  automatically on the next research pass.
                </p>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <a
                    href="/ai-settings"
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                  >
                    Open AI Settings
                  </a>
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/investors/${investor.id}/research`);
                        toast.success('Research re-queued. Dossier will populate if Claude is enabled.');
                      } catch (e: any) {
                        toast.error(e?.response?.data?.detail || 'Re-queue failed');
                      }
                    }}
                    className="inline-flex items-center px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-50"
                  >
                    Re-run research
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'completion' && (
          <section className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Research checklist</h3>
              {completion && (
                <span className="text-xs text-gray-600">
                  {completion.earned} / 100 (free max: {completion.free_max})
                </span>
              )}
            </div>
            {completion ? (
              <>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Research completion</span>
                    <span className="font-medium text-gray-900">{completion.percent}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${completion.percent >= 80 ? 'bg-green-500' : completion.percent >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                      style={{ width: `${completion.percent}%` }}
                    />
                  </div>
                </div>
                <ul className="divide-y divide-gray-100">
                  {completion.checklist.map((item) => (
                    <li
                      key={item.key}
                      className="flex items-center justify-between py-2 text-sm gap-3"
                    >
                      <span className="flex items-center gap-2 min-w-0 flex-1">
                        {item.present ? (
                          <span className="inline-block w-4 h-4 rounded-full bg-green-500 text-white text-[10px] leading-4 text-center flex-shrink-0">
                            ✓
                          </span>
                        ) : (
                          <span className="inline-block w-4 h-4 rounded-full bg-gray-200 flex-shrink-0" />
                        )}
                        <span className={item.present ? 'text-gray-900' : 'text-gray-500'}>
                          {item.label}
                        </span>
                        {item.source_class === 'claude' && (
                          <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            Claude
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-gray-500 min-w-[80px] text-right">
                          {item.present && item.filled_by ? (
                            item.filled_by.source_url ? (
                              <a
                                href={item.filled_by.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline"
                                title={`Source: ${formatSourceLabel(item.filled_by.source_type)}`}
                              >
                                {formatSourceLabel(item.filled_by.source_type)}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span title={`Source: ${formatSourceLabel(item.filled_by.source_type)}`}>
                                {formatSourceLabel(item.filled_by.source_type)}
                              </span>
                            )
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500 w-10 text-right">{item.weight}%</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-gray-500 italic">Loading completion checklist…</p>
            )}
          </section>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-4">
            <InvestorMatchInsights investor={investor} matches={matches} />
            <InvestorMatchFeedbackBox investorId={investor.id} />

            <section className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Matches</h3>
                  <p className="text-xs text-gray-500">
                    Top scoring startups and projects for this investor.
                  </p>
                </div>
                <button
                  onClick={handleGenerateInvestorMatches}
                  disabled={generatingMatches}
                  className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {generatingMatches ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      Generate matches
                    </>
                  )}
                </button>
              </div>

              {matchesLoading ? (
                <div className="flex items-center text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading matches...
                </div>
              ) : matches.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No matches yet. Click "Generate matches" to score this investor
                  against startups and projects.
                </p>
              ) : (
                <div className="space-y-3">
                  {matches.map((m) => (
                    <MatchCard key={m.id} match={m} hideInvestor />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {activeTab === 'notes' && (
          <section className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Notes</h3>
            {investor.notes ? (
              <p className="whitespace-pre-wrap text-sm text-gray-800">{investor.notes}</p>
            ) : (
              <p className="text-sm text-gray-500">No notes yet.</p>
            )}
          </section>
        )}

      </div>
    </DashboardLayout>
  );
};

export default InvestorDetailPage;
